import type {
  CameraWaypoint,
  DeskLoginConfig,
  DesktopConfig,
  MailConfig,
  MfitChatConfig,
  MfitCloudDashConfig,
  MfitConsentConfig,
  ScenarioSpec,
  ScoringEvent,
  TerminalConfig,
} from "./types";
import { officeUnderAttackScenario } from "./office-under-attack.scenario";
import { holidayCallScenario } from "./holiday-call.scenario";
import { quishingRestaurantScenario } from "./quishing-restaurant.scenario";

/**
 * The Message From IT — Teams, Slack and malicious app consent.
 *
 * Office (from office-under-attack): reception terminal, Alex's desk PC.
 * Beach bar + iPad (from holiday-call). Hacker room (from quishing-restaurant).
 * The donor scenarios are only read; every beat/device/waypoint id here is new
 * (prefix mf_ / wp_mf_) so no App.tsx special case for the donors fires.
 *
 * Endings: Alex accepts the OAuth consent (attack successful) or stops it at
 * the Slack message / the consent screen (attack prevented). Prevented paths
 * still see the attacker's side as "What would have happened".
 */

const OUA = officeUnderAttackScenario;
const OUA_ENV = OUA.scene.environment;
const BEACH_ENV = holidayCallScenario.scene.environments?.find((e) => e.id === "beach")?.environment;
const HACKER_ENV = quishingRestaurantScenario.scene.environments?.find((e) => e.id === "hacker")?.environment;
if (!BEACH_ENV) throw new Error('holiday-call environment "beach" not found');
if (!HACKER_ENV) throw new Error('quishing-restaurant environment "hacker" not found');

function waypoint(scenario: ScenarioSpec, id: string, newId: string, durationS?: number): CameraWaypoint {
  const value = scenario.scene.waypoints.find((w) => w.id === id);
  if (!value) throw new Error(`${scenario.id} waypoint "${id}" not found`);
  return { ...value, id: newId, ...(durationS == null ? {} : { durationS }) };
}

const ASSETS = "/cdn/sim/message-from-it";

const OFFICE_ENV = {
  gltfUrl: OUA_ENV.gltfUrl,
  gltfScale: OUA_ENV.gltfScale,
  gltfPosition: OUA_ENV.gltfPosition,
  gltfRotationDeg: OUA_ENV.gltfRotationDeg,
  gltfColliders: OUA_ENV.gltfColliders,
  procedural: OUA_ENV.procedural,
  enableSplat: false,
  enablePano: false,
  backdropColor: OUA_ENV.backdropColor,
  exposure: OUA_ENV.exposure,
  splat: {},
  deskPc: OUA_ENV.deskPc,
  receptionTerminal: OUA_ENV.receptionTerminal,
};

const BEACH = {
  ...BEACH_ENV,
  beachIpad: "notify" as const,
  beachIpadNotice: { time: "14:21", app: "Slack", from: "Nina Patel", text: "Hey Alex, so sorry to disturb your holiday!" },
  beachDrink: "cocktail" as const,
};

const ALEX = {
  name: "Alex Morgan",
  email: "alex.morgan@northbayfinancial.com",
  avatarUrl: `${ASSETS}/img/alex.jpg`,
};
const NINA = { name: "Nina Patel", role: "Procurement · NorthBay Financial", avatarUrl: `${ASSETS}/img/nina.jpg` };
const FAKE_LINK = "securedocs-northbay.com/v/8f3k2";
const REPLAY_NOTE = "If Alex had clicked Accept, this is what the attacker would have seen.";

// ---------------------------------------------------------------- scoring
const EVENTS: ScoringEvent[] = [
  { id: "mf_watched_security_minute", label: "Watched the IT security minute", kind: "positive", weight: 5, dimension: "information", collectEvidence: true },
  {
    id: "mf_replied_fake_it",
    label: "Replied YES to an external 'IT Service Desk' in Teams",
    kind: "risk",
    weight: 25,
    dimension: "identity",
    collectEvidence: true,
    detail: "The account was marked External. A support session is how attackers get remote control of a PC.",
  },
  { id: "mf_reported_fake_it", label: "Reported the fake IT message in Teams", kind: "positive", weight: 15, dimension: "identity", collectEvidence: true },
  { id: "mf_verified_it", label: "Checked with the real IT service desk", kind: "positive", weight: 15, dimension: "identity", collectEvidence: true },
  { id: "mf_ignored_fake_it", label: "Ignored the fake IT message", kind: "positive", weight: 5, dimension: "identity" },
  {
    id: "mf_opened_shared_link",
    label: "Opened the SecureDocs link from Slack",
    kind: "risk",
    weight: 20,
    dimension: "information",
    collectEvidence: true,
    detail: "The link did not go to NorthBay's own file storage, and the request was urgent and unexpected.",
  },
  { id: "mf_verified_nina", label: "Called Nina on a known number before opening the file", kind: "positive", weight: 25, dimension: "information", collectEvidence: true },
  { id: "mf_reported_slack", label: "Reported Nina's message to IT Security", kind: "positive", weight: 25, dimension: "information", collectEvidence: true },
  {
    id: "mf_granted_app",
    label: "Accepted SecureDocs Workspace's permission request",
    kind: "risk",
    weight: 70,
    dimension: "information",
    collectEvidence: true,
    detail: "The app could read Alex's mailbox and files, send email as Alex, and keep that access.",
  },
  { id: "mf_cancelled_consent", label: "Cancelled the permission request", kind: "positive", weight: 15, dimension: "information", collectEvidence: true },
  { id: "mf_cancelled_reported", label: "Cancelled the permission request and reported it", kind: "positive", weight: 25, dimension: "information", collectEvidence: true },
];

// ---------------------------------------------------------------- devices
const terminal: TerminalConfig = {
  title: "NORTHBAY FINANCIAL — SECURITY BRIEFING",
  offLabel: "Switch on the terminal",
  voiceId: "nova",
  lines: [
    "This course is about trust inside the tools you use every day: Teams, Slack, and the documents shared through them.",
    "Attackers now pretend to be IT support or colleagues inside these apps. Microsoft has documented IT-helpdesk impersonation through Teams.",
    "Its research also shows that malicious apps can get access to company email and documents through deceptive permission requests.",
    "You are Alex Morgan from Operations. It is Friday afternoon, your last day before a holiday. Head to your desk.",
  ],
  continueLabel: "Go to my desk",
};

const deskLogin: DeskLoginConfig = {
  screenTitle: "NorthBay Financial — locked",
  userName: ALEX.name,
  hint: "Your sign-in details are in the note on the right. Click it to fill them in.",
  credential: { email: ALEX.email, password: "Harbour-Light-42", source: "Your sign-in" },
  continueLabel: "Sign In",
};

const intranet: MfitChatConfig = {
  frame: "desktop",
  mode: "intranet",
  clock: "16:47",
  date: "Fri 15 May",
  intranet: {
    url: "intranet.northbay.internal",
    siteName: "NorthBay Intranet",
    navItems: ["News", "People", "IT help", "Policies"],
    headline: "Good afternoon, Alex",
    news: [
      { title: "Supplier renewal on track", body: "Procurement expects the renewal pack to be signed next week." },
      { title: "Floor 3 closed Monday morning", body: "Maintenance from 7:00 to 10:00." },
      { title: "IT service desk", body: "Call +41 44 500 22 00 or open a ticket in the IT portal." },
    ],
    notice: {
      kicker: "IT Security · Your weekly security minute",
      title: "Can you trust messages in Teams and Slack?",
      body: "Attackers increasingly use collaboration apps to pretend to be colleagues and IT support staff. This week's film takes about four minutes.",
      watchLabel: "Watch the video",
      dismissLabel: "Dismiss",
    },
    watchEventId: "mf_watched_security_minute",
    video: {
      title: "Security minute: trust in Teams and Slack",
      src: "/cdn/sim/awareness-videos/chat-phishing/chat-phishing.mp4",
      poster: `${ASSETS}/img/film-poster.jpg`,
      tip: "A message arriving inside a trusted app does not mean its sender is trustworthy. Check who it really is before you act.",
      closeLabel: "Close the video",
    },
  },
  messages: [],
  guide: {
    desktop: { title: "Check the intranet", body: "Before you leave, open the company intranet. Click Browser on the desktop.", actionLabel: "Browser" },
    intranet: { title: "Your weekly security minute", body: "IT Security has posted a short film. Watch it, or dismiss it and carry on." },
    video: { title: "Security minute", body: "Watch the film. You can click Close the video at any time.", actionLabel: "Close the video" },
  },
};

const teams: MfitChatConfig = {
  frame: "desktop",
  mode: "chat",
  skin: "teams",
  clock: "16:49",
  date: "Fri 15 May",
  notice: { app: "Teams", from: "IT Service Desk (External)", text: "Hi Alex, we're completing a security update for collaboration accounts before Monday." },
  contact: { name: "IT Service Desk", initials: "IT", external: true },
  me: { name: ALEX.name, avatarUrl: ALEX.avatarUrl },
  messages: [
    {
      id: "it1",
      from: "IT Service Desk",
      initials: "IT",
      time: "16:49",
      text:
        "Hi Alex, we're completing a security update for collaboration accounts before Monday.\n\nWe noticed your account still needs verification. Could you spare two minutes for a quick support session before you leave?\n\nReply YES and we'll connect you with a technician.",
    },
  ],
  choicesPrompt: "What do you do?",
  choices: [
    {
      id: "yes",
      label: "Reply YES",
      blurb: "Two minutes and it's done.",
      key: "replied",
      eventId: "mf_replied_fake_it",
      evidence: "Replied YES to an external 'IT Service Desk'",
      reply: [
        { id: "me_yes", from: ALEX.name, time: "16:50", text: "YES", self: true, avatarUrl: ALEX.avatarUrl },
        {
          id: "it2",
          from: "IT Service Desk",
          initials: "IT",
          time: "16:50",
          text: "Great, thank you! A technician will send you a remote session link in a few minutes. Please keep Teams open.",
        },
      ],
      result: {
        title: "You agreed to a support session",
        body: "No link arrives before you leave, so nothing happens this time. A real attacker would have sent you a remote-access tool next.",
        tone: "warn",
        continueLabel: "Continue",
      },
    },
    { id: "inspect_sender", label: "Check who sent it", blurb: "Open the sender's details.", key: "inspect", inspect: true },
    {
      id: "report",
      label: "Report the message",
      blurb: "Use Teams' Report option.",
      key: "reported",
      eventId: "mf_reported_fake_it",
      evidence: "Reported the external 'IT Service Desk' message",
      result: {
        title: "Reported to IT Security",
        body: "IT Security confirms: this is not NorthBay's service desk. They block the external account and warn other staff.",
        tone: "good",
        continueLabel: "Continue",
      },
    },
    {
      id: "verify",
      label: "Call the IT service desk",
      blurb: "Use the number on the intranet, not one from the chat.",
      key: "verified",
      eventId: "mf_verified_it",
      evidence: "Called the real IT service desk",
      result: {
        title: "IT knows nothing about it",
        body: "The real service desk has no update planned and no ticket for you. They ask you to report the message, then they block the account.",
        tone: "good",
        continueLabel: "Continue",
      },
    },
    {
      id: "dismiss",
      label: "Ignore it and leave",
      blurb: "You're about to go on holiday.",
      key: "dismissed",
      eventId: "mf_ignored_fake_it",
      evidence: "Ignored the message",
      result: {
        title: "Message ignored",
        body: "Nothing happens. But IT does not know someone is pretending to be them, so a colleague might reply instead.",
        tone: "neutral",
        continueLabel: "Continue",
      },
    },
  ],
  senderCard: {
    title: "About this sender",
    rows: [
      { label: "Display name", value: "IT Service Desk" },
      { label: "Account", value: "servicedesk@nb-itsupport.com", warn: true },
      { label: "Organisation", value: "External: not NorthBay Financial", warn: true },
      { label: "First contact", value: "Today, 16:49" },
    ],
    note: "NorthBay's IT team writes from @northbayfinancial.com accounts. Anyone can name a Teams account \"IT Service Desk\".",
  },
  guide: {
    desktop: { title: "A Teams message", body: "Teams just pinged. Click the notification or the Teams icon to read it.", actionLabel: "Teams" },
    chat: {
      title: "A message from IT?",
      body: "Read the message. Then decide what to do: reply, check who sent it, report it, call the service desk, or ignore it.",
    },
    sender: { title: "Who is this?", body: "Read the sender's details. Then click Back to the message.", actionLabel: "Back to the message" },
    result: { title: "What happened", body: "Read what happened. Then click Continue.", actionLabel: "Continue" },
  },
};

const teamsReview: MfitChatConfig = {
  ...teams,
  notice: undefined,
  review: {
    kicker: "A second look",
    title: "Who sent this message?",
    prompt: "Look at the message again. Click the sender's name to see who really sent it.",
    revealIntro: "This is who really sent it. Four things should make you stop:",
    issues: [
      { label: "An External account", detail: "Teams marks the sender as External: the account belongs to an organisation outside NorthBay Financial." },
      { label: "Not NorthBay's address", detail: "It writes from servicedesk@nb-itsupport.com. NorthBay's IT team uses @northbayfinancial.com." },
      { label: "A request you did not expect", detail: "No security update was announced, and real IT staff do not ask you to reply YES for a support session." },
      { label: "Time pressure", detail: "\"Before Monday\" and \"before you leave\" push you to act before you check." },
    ],
    continueLabel: "Continue",
  },
  guide: {
    review: { title: "A second look", body: "Click the sender's name in the message to see who really sent it." },
    review_sender: { title: "What gave it away", body: "Read the four warning signs. Then click Continue.", actionLabel: "Continue" },
  },
};

const slackHandover: MfitChatConfig = {
  frame: "desktop",
  mode: "chat",
  skin: "slack",
  clock: "16:52",
  date: "Fri 15 May",
  workspace: "NorthBay Financial",
  channel: "#supplier-renewal",
  channelTopic: "Renewal pack due Monday 15:00",
  me: { name: ALEX.name, avatarUrl: ALEX.avatarUrl },
  messages: [
    {
      id: "n1",
      from: NINA.name,
      avatarUrl: NINA.avatarUrl,
      time: "16:52",
      text: "Enjoy your holiday, Alex! I'll handle the supplier renewal pack. Hopefully we won't need you before Wednesday.",
    },
  ],
  compose: { text: "Thanks! Everything should be ready. I have my iPad if there's a genuine emergency.", sendLabel: "Send", time: "16:53" },
  statusStep: { label: "Set a status before you go", emoji: "🌴", text: "On holiday — returning Wednesday", setLabel: "Set status" },
  lockLabel: "Lock PC and leave",
  outro: { lines: ["🌴 Alex Morgan · On holiday — returning Wednesday"], ping: true },
  guide: {
    desktop: { title: "Finish the handover", body: "Nina takes over the supplier renewal while you are away. Click Slack.", actionLabel: "Slack" },
    chat: { title: "Reply to Nina", body: "Your reply is ready in the message box. Click Send.", actionLabel: "Send" },
    status: { title: "Set your status", body: "Let colleagues know you are away. Click Set status.", actionLabel: "Set status" },
    lock: { title: "Time to go", body: "Click Lock PC and leave.", actionLabel: "Lock PC and leave" },
    outro: { title: "Out of office", body: "Alex locks the PC and leaves for the holiday." },
  },
};

const ipadSlack: MfitChatConfig = {
  frame: "ipad",
  mode: "chat",
  skin: "slack",
  lockTime: "14:21",
  lockDate: "Monday 18 May",
  notice: { app: "Slack", from: NINA.name, text: "Hey Alex, so sorry to disturb your holiday!", time: "now" },
  workspace: "NorthBay Financial",
  contact: { name: NINA.name, role: NINA.role, avatarUrl: NINA.avatarUrl },
  me: { name: ALEX.name, avatarUrl: ALEX.avatarUrl },
  messages: [
    {
      id: "dm1",
      from: NINA.name,
      avatarUrl: NINA.avatarUrl,
      time: "14:21",
      text:
        "Hey Alex, so sorry to disturb your holiday!\n\nThe supplier has requested a last-minute change to the renewal agreement. Finance needs your sign-off before 15:00.\n\nI've uploaded the revised document. Could you quickly confirm page 2? Should take 30 seconds.",
      attachment: { name: "Supplier_Renewal_Final.pdf", source: "Shared through SecureDocs", size: "2 pages", displayUrl: FAKE_LINK },
    },
  ],
  choicesPrompt: "What do you do?",
  choices: [
    { id: "open", label: "Open the document", blurb: "It's Nina, and it takes 30 seconds.", key: "open", eventId: "mf_opened_shared_link", evidence: "Opened the SecureDocs link" },
    { id: "inspect_link", label: "Check the link first", blurb: "See where the file really is.", key: "inspect", inspect: true },
    {
      id: "verify",
      label: "Call Nina on her mobile",
      blurb: "Use the number in the company directory.",
      key: "verify",
      eventId: "mf_verified_nina",
      evidence: "Called Nina on her directory number",
      result: {
        title: "Nina didn't send it",
        body: "Nina picks up: \"That wasn't me! I haven't touched the renewal today.\" Someone has taken over her Slack account. You both report it to IT Security.",
        tone: "good",
        continueLabel: "Continue",
      },
    },
    {
      id: "report",
      label: "Report it to IT Security",
      blurb: "Forward the message with the report option.",
      key: "report",
      eventId: "mf_reported_slack",
      evidence: "Reported Nina's message to IT Security",
      result: {
        title: "Reported to IT Security",
        body: "IT Security calls Nina. She never sent it: her Slack account was taken over. They sign her out everywhere and block the SecureDocs link.",
        tone: "good",
        continueLabel: "Continue",
      },
    },
  ],
  senderCard: {
    title: "Nina Patel",
    rows: [
      { label: "Workspace", value: "NorthBay Financial" },
      { label: "Title", value: "Procurement" },
      { label: "Local time", value: "14:21" },
    ],
    note: "This is Nina's real account. A real account can still be used by someone else.",
  },
  linkCard: {
    title: "Where the link goes",
    rows: [
      { label: "File", value: "Supplier_Renewal_Final.pdf" },
      { label: "Shared through", value: "SecureDocs" },
      { label: "Link address", value: FAKE_LINK, warn: true },
      { label: "NorthBay files live on", value: "northbayfinancial.sharepoint.com" },
    ],
    note: "The link does not go to NorthBay's own file storage. That alone does not prove it is malicious, but it is a reason to check with Nina another way.",
  },
  guide: {
    ipad_lock: { title: "A message on your iPad", body: "Drag to look around the bar. When you are ready, tap the Slack notification on the iPad." },
    ipad_away: { title: "Look back at the iPad", body: "Turn back toward the iPad on the bar to read the notification." },
    chat: {
      title: "Nina needs a quick sign-off",
      body: "Read Nina's message. Then decide: open the document, check the link first, call Nina, or report it.",
    },
    sender: { title: "Nina's profile", body: "Read the profile. Then click Back to the message.", actionLabel: "Back to the message" },
    link: { title: "Where does the link go?", body: "Read where the file really is. Then click Back to the message.", actionLabel: "Back to the message" },
    result: { title: "What happened", body: "Read what happened. Then click Continue.", actionLabel: "Continue" },
  },
};

const consentBase: MfitConsentConfig = {
  url: FAKE_LINK,
  clock: "14:22",
  viewer: {
    brand: "SecureDocs",
    fileName: "Supplier_Renewal_Final.pdf",
    sharedBy: NINA.name,
    body: "This document is protected. Sign in with your work account to view it.",
    signInLabel: "Sign in with Microsoft",
    altLabels: ["Sign in with Google"],
  },
  signin: { name: ALEX.name, account: ALEX.email, heading: "Pick an account", pickHint: "to continue to SecureDocs Workspace" },
  consent: {
    appName: "SecureDocs Workspace",
    publisher: "securedocs-northbay.com",
    appDomain: "securedocs-northbay.com",
    unverifiedLabel: "unverified",
    intro: "This app would like to:",
    permissions: [
      { title: "Read your email", detail: "Read every message in your mailbox." },
      { title: "Read files you have access to", detail: "Open documents in OneDrive, SharePoint and Teams that you can open." },
      { title: "Send email on your behalf", detail: "Send messages that appear to come from you." },
      { title: "Maintain access to data you have given it access to", detail: "Keep this access even when you are not using the app." },
    ],
    footer:
      "Accepting these permissions means that you allow this app to use your data as specified in their terms of service and privacy statement. You can change these permissions at myapps.microsoft.com.",
    cancelLabel: "Cancel",
    acceptLabel: "Accept",
    appInfoLabel: "Show app details",
    appInfo: {
      title: "App details",
      rows: [
        { label: "App", value: "SecureDocs Workspace" },
        { label: "Publisher", value: "Unverified", warn: true },
        { label: "App website", value: "securedocs-northbay.com", warn: true },
        { label: "Asks for", value: "Your mailbox, your files, sending as you, lasting access", warn: true },
      ],
      note: "A document viewer only needs to open one file. This app asks for your whole mailbox and permission to send email as you.",
    },
  },
  acceptedLine: "Document access granted. Loading preview…",
  acceptEventId: "mf_granted_app",
  cancel: {
    title: "Request cancelled",
    body: "SecureDocs Workspace got no access. Do you want to report the request to IT Security?",
    reportLabel: "Report to IT Security",
    closeLabel: "Just close it",
    reportEventId: "mf_cancelled_reported",
    cancelEventId: "mf_cancelled_consent",
    reportedNote: "IT Security has your report. They check Nina's account and block the SecureDocs app for everyone at NorthBay.",
  },
  guide: {
    viewer: { title: "SecureDocs", body: "The document viewer asks you to sign in. Click Sign in with Microsoft.", actionLabel: "Sign in with Microsoft" },
    signin: { title: "Microsoft sign-in", body: "This is a real Microsoft page. Pick your account to continue.", actionLabel: ALEX.name },
    consent: { title: "Permissions requested", body: "Read what SecureDocs Workspace is asking for. Then click Accept or Cancel." },
    appinfo: { title: "App details", body: "Read the app details. Then click Back to the permissions.", actionLabel: "Back to the permissions" },
    accepted: { title: "Loading preview…", body: "The document is loading." },
    cancelled: { title: "Request cancelled", body: "Report the request to IT Security, or just close it." },
    reported: { title: "Reported", body: "Click Continue.", actionLabel: "Continue" },
    viewer_replay: { title: "The document you would have opened", body: "This is the page the link would have opened. Click Sign in with Microsoft.", actionLabel: "Sign in with Microsoft" },
    signin_replay: { title: "A real Microsoft page", body: "Pick your account to continue.", actionLabel: ALEX.name },
    consent_replay: {
      title: "The permission screen",
      body: "This is what SecureDocs Workspace would have asked for. Click Accept to see what the attacker would have got.",
      actionLabel: "Accept",
    },
  },
};

const dashTimeline: MfitCloudDashConfig = {
  view: "timeline",
  kicker: "Attack log · simulation",
  title: "op-renewal / timeline",
  operator: "operator: kestrel",
  voiceId: "onyx",
  narration: [
    { text: "While Alex was enjoying a holiday, someone else was enjoying new access to the company's data." },
    { text: "First, the attacker took over Nina's Slack account and read the supplier renewal conversation.", focus: "s1" },
    { text: "Then they pretended to be IT in Teams. Alex did not start a support session, so that attempt failed.", focus: "s2" },
    { text: "So they switched to the identity Alex trusts: Nina. They knew the project, the deadline, and that Alex was on holiday.", focus: "s3" },
    { text: "On the beach, Alex approved the app's access from the iPad. No password was stolen.", focus: "s4" },
  ],
  steps: [
    { id: "s1", title: "Compromised a colleague's Slack account", body: "Read #supplier-renewal on Nina's account: the deadline, the documents, Alex's holiday." },
    { id: "s2", title: "Attempted IT impersonation in Teams", body: "An external 'IT Service Desk' asked Alex for a support session.", state: "failed" },
    { id: "s3", title: "Switched to the trusted Slack identity", body: "Sent Alex an urgent sign-off request as Nina, with a SecureDocs link." },
    { id: "s4", title: "Malicious app access approved", body: "Alex consented to company-data access from the iPad.", state: "success" },
  ],
  continueLabel: "See what the attacker gets",
};

const dashAccess: MfitCloudDashConfig = {
  view: "access",
  kicker: "Cloud access · granted by Alex",
  title: "op-renewal / alex.morgan",
  operator: "operator: kestrel",
  voiceId: "onyx",
  narration: [
    { text: "This is what the consent gave the attacker's app: Alex's mailbox, Alex's files, and the right to send email as Alex.", focus: "a_mail" },
    { text: "And the access stays, even when Alex is not using the app.", focus: "a_auth" },
    { text: "The attacker searches the mailbox. An email called Supplier Banking Details, marked confidential.", focus: "f1" },
    { text: "A customer pricing spreadsheet, and the contract renewal document.", focus: "f2" },
    { text: "From the supplier conversation they learn who in Finance pays the renewal.", focus: "f3" },
    { text: "Now they write to Finance, as Alex, from Alex's real mailbox.", focus: "draft" },
    { text: "No administrator rights. No hacked password. One person's mailbox and files are enough to set up a payment fraud." },
  ],
  access: [
    { id: "a_mail", label: "Mailbox", value: "Read messages", detail: "Every email Alex has sent and received." },
    { id: "a_files", label: "Documents", value: "Read accessible files", detail: "OneDrive, SharePoint and Teams files Alex can open." },
    { id: "a_send", label: "Email identity", value: "Send as Alex", detail: "Messages go out from Alex's real address." },
    { id: "a_auth", label: "Authorization", value: "Ongoing access possible", detail: "Works without Alex's password and survives a password change." },
  ],
  findings: [
    { id: "f1", kind: "mail", title: "Supplier Banking Details — Confidential", meta: "Inbox · 12 Mar · from Harlow Logistics", lines: ["IBAN on file for the renewal payments", "Contact: accounts@harlow-logistics.example"] },
    { id: "f2", kind: "sheet", title: "Customer_Pricing_2026.xlsx", meta: "SharePoint · Operations" },
    { id: "f3", kind: "doc", title: "Contract_Renewal_Harlow.docx", meta: "Finance contact: Marta Lindqvist (Accounts Payable)" },
  ],
  draft: {
    id: "draft",
    from: "Alex Morgan <alex.morgan@northbayfinancial.com>",
    to: "Marta Lindqvist (Finance); cc: Alex Morgan",
    subject: "Supplier renewal — updated payment instructions",
    body: "Hi Finance,\n\nFollowing the changes to our supplier agreement, please verify the updated payment instructions before processing the renewal.\n\nThanks,\nAlex",
  },
  continueLabel: "Continue",
};

const dashDenied: MfitCloudDashConfig = {
  view: "denied",
  kicker: "Attack log · simulation",
  title: "op-renewal / timeline",
  operator: "operator: kestrel",
  voiceId: "onyx",
  narration: [
    { text: "The attacker waits for the consent to come through.", focus: "s3" },
    { text: "It never does. Alex cancelled the request, so the app got no access at all.", focus: "s4" },
    { text: "No mailbox, no files, no way to send email as Alex.", focus: "denied" },
  ],
  steps: [
    { id: "s1", title: "Compromised a colleague's Slack account", body: "Read #supplier-renewal on Nina's account: the deadline, the documents, Alex's holiday." },
    { id: "s2", title: "Attempted IT impersonation in Teams", body: "An external 'IT Service Desk' asked Alex for a support session.", state: "failed" },
    { id: "s3", title: "Switched to the trusted Slack identity", body: "Sent Alex an urgent sign-off request as Nina, with a SecureDocs link." },
    { id: "s4", title: "App access request", body: "Alex cancelled the permission request on the iPad.", state: "denied" },
  ],
  deniedNote: "Authorization denied. No new access was obtained.",
  continueLabel: "Continue",
};

const dashNetwork: MfitCloudDashConfig = {
  view: "network",
  kicker: "Next targets · simulation",
  title: "op-renewal / contacts",
  operator: "operator: kestrel",
  voiceId: "onyx",
  narration: [
    { text: "The attacker never needed to steal Alex's password." },
    { text: "The authorization granted on the beach allowed the malicious app to read information and send messages on Alex's behalf.", focus: "node_0" },
    { text: "And every person in Alex's mailbox now receives emails from someone they trust.", focus: "node_1" },
    { text: "Marta in Finance is next.", focus: "caption" },
  ],
  network: {
    center: "Alex",
    centerRole: "Operations · mailbox access",
    nodes: [
      { name: "Marta Lindqvist", role: "Finance · Accounts Payable", next: true },
      { name: "Nina Patel", role: "Procurement" },
      { name: "Harlow Logistics", role: "Supplier" },
      { name: "Daniel Okafor", role: "Head of Operations" },
      { name: "Finance team", role: "Shared mailbox" },
    ],
    caption: "Another employee may now be the next target.",
  },
  continueLabel: "Continue",
};

const desktopBack: DesktopConfig = {
  clock: "08:41",
  date: "Wed 20 May",
  icons: [
    { id: "mail", label: "Outlook", icon: "outlook", opens: "mail" },
    { id: "teams", label: "Teams", icon: "teams", opens: "teams" },
    { id: "slack", label: "Slack", icon: "slack", opens: "slack" },
    { id: "browser", label: "Browser", icon: "chrome", opens: "browser" },
  ],
  taskbar: [
    { id: "mail", label: "Outlook", icon: "outlook", opens: "mail" },
    { id: "teams", label: "Teams", icon: "teams", opens: "teams" },
    { id: "slack", label: "Slack", icon: "slack", opens: "slack" },
    { id: "browser", label: "Browser", icon: "chrome", opens: "browser" },
  ],
};

const mailBack: MailConfig = {
  appTitle: "Outlook",
  folders: ["Inbox", "Sent Items", "Drafts", "Archive"],
  emptyTitle: "Welcome back",
  emptyHint: "Select a message to read it.",
  emails: [
    {
      id: "fraud",
      from: "Alex Morgan — Operations",
      fromAddress: ALEX.email,
      to: "Marta Lindqvist (Finance); cc: Alex Morgan",
      subject: "Supplier renewal — updated payment instructions",
      preview: "Hi Finance, following the changes to our supplier agreement…",
      body:
        "Hi Finance,\n\nFollowing the changes to our supplier agreement, please verify the updated payment instructions before processing the renewal.\n\nThanks,\nAlex",
      receivedAt: "Tue 16:12",
      unread: true,
      initials: "AM",
      attachments: [{ id: "pi", name: "Updated_Payment_Instructions.pdf", type: "pdf", size: "88 KB" }],
      lure: true,
    },
    {
      id: "finance_reply",
      from: "Marta Lindqvist — Finance",
      fromAddress: "marta.lindqvist@northbayfinancial.com",
      to: ALEX.name,
      subject: "RE: Supplier renewal — updated payment instructions",
      preview: "Thanks Alex, we'll update the bank details and release the payment on Thursday.",
      body: "Thanks Alex,\n\nWe'll update the bank details and release the payment on Thursday.\n\nMarta",
      receivedAt: "Tue 16:40",
      unread: true,
      initials: "ML",
    },
    {
      id: "welcome",
      from: "Daniel Okafor",
      fromAddress: "daniel.okafor@northbayfinancial.com",
      subject: "Welcome back!",
      preview: "Hope the holiday was great. Catch-up at 10?",
      body: "Hi Alex,\n\nHope the holiday was great. Catch-up at 10?\n\nDaniel",
      receivedAt: "Wed 08:02",
      initials: "DO",
    },
  ],
};

// ---------------------------------------------------------------- scenario
export const messageFromItScenario: ScenarioSpec = {
  version: 1,
  id: "message-from-it",
  title: "The Message From IT",
  topic: "Teams, Slack and app consent phishing",
  language: "en",
  summary: {
    goal: "Spot when a trusted chat app or a real sign-in screen is being used against you.",
    youDo: "Handle a Teams message from 'IT', a Slack request on holiday and a Microsoft permission screen, then watch the attack from the attacker's side.",
    duration: "12-15 min, plus an optional 4-minute film",
    tags: ["verification", "spear_phishing", "fake_login_pages", "incident_reporting", "personal_devices"],
    audience: ["all_staff", "finance", "travellers"],
    needs: ["sound"],
  },
  ambientUrl: "/audio/gymnopedie-kevin-macleod.mp3",
  ambientVolume: 0.14,
  music: "none",
  passThreshold: 60,
  riskMeter: false,
  exposureMap: true,
  allowFreeExplore: false,

  scene: {
    environment: OFFICE_ENV,
    environments: [
      { id: "office", environment: OFFICE_ENV, ambientUrl: "/audio/gymnopedie-kevin-macleod.mp3", ambientVolume: 0.14, music: "none" },
      { id: "beach", environment: BEACH, ambientUrl: "/audio/beach-ambience.mp3", ambientVolume: 0.22, music: "none" },
      { id: "hacker", environment: HACKER_ENV, ambientUrl: "", music: "dark" },
    ],
    props: [],
    waypoints: [
      waypoint(OUA, "wp_reception_wide", "wp_mf_reception"),
      waypoint(OUA, "wp_terminal_close", "wp_mf_terminal"),
      waypoint(OUA, "wp_desk_signin", "wp_mf_desk"),
      waypoint(OUA, "wp_desk_signin", "wp_mf_desk_cut", 0),
      waypoint(holidayCallScenario, "wp_bar_ocean", "wp_mf_ocean", 0),
      waypoint(holidayCallScenario, "wp_bar_establish", "wp_mf_bar_establish"),
      { id: "wp_mf_bar_seat", position: [-0.78, 1.56, -2.72], target: [-1.02, 1.3, -1.5], fov: 54, durationS: 3.2, ease: "easeInOut" },
      waypoint(quishingRestaurantScenario, "wp_hacker_establish", "wp_mf_hacker"),
      waypoint(quishingRestaurantScenario, "wp_hacker_laptop", "wp_mf_hacker_laptop"),
    ],
    initialWaypointId: "wp_mf_reception",
  },

  devices: [
    { id: "mf_terminal_kiosk", kind: "terminal", config: terminal },
    { id: "mf_desklogin", kind: "desklogin", config: deskLogin },
    { id: "mf_pc_intranet", kind: "mfitchat", config: intranet },
    { id: "mf_pc_teams", kind: "mfitchat", config: teams },
    { id: "mf_pc_teams_review", kind: "mfitchat", config: teamsReview },
    { id: "mf_pc_slack", kind: "mfitchat", config: slackHandover },
    { id: "mf_ipad_slack", kind: "mfitchat", config: ipadSlack },
    { id: "mf_ipad_consent", kind: "mfitconsent", config: consentBase },
    { id: "mf_ipad_consent_replay", kind: "mfitconsent", config: { ...consentBase, replay: true } },
    { id: "mf_dash_timeline", kind: "mfitclouddash", config: dashTimeline },
    { id: "mf_dash_access", kind: "mfitclouddash", config: dashAccess },
    { id: "mf_dash_denied", kind: "mfitclouddash", config: dashDenied },
    { id: "mf_dash_network", kind: "mfitclouddash", config: dashNetwork },
    { id: "mf_desktop_back", kind: "desktop", config: desktopBack },
    { id: "mf_mail_back", kind: "mail", config: mailBack },
  ],

  voiceAttackers: {
    message_from_it: {
      persona: { name: "Attacker", role: "Collaboration-app impersonation", pretext: "it_support", voiceId: "onyx" },
      goal: "Get a malicious app approved on Alex's work account",
      opening: "",
      allowedTopics: [],
      scoringEvents: EVENTS,
    },
  },

  initialBeatId: "mf_intro",
  beats: [
    {
      id: "mf_intro",
      type: "intro",
      title: "The Message\nFrom IT",
      kicker: "Teams, Slack and app consent",
      highlight: "a holiday",
      icon: "shield",
      ctaLabel: "Start",
      guide: {
        title: "Friday, 16:45 at NorthBay Financial",
        body: "You are Alex Morgan from Operations. Tomorrow you leave for a holiday, and a few messages still need your attention before you go.",
      },
    },
    { id: "mf_cam_terminal", type: "camera", waypointId: "wp_mf_terminal", walkSfx: true },
    {
      id: "mf_terminal",
      type: "device",
      deviceId: "mf_terminal_kiosk",
      guide: { title: "Security briefing", body: "Switch on the terminal at the front desk. You can skip the briefing at any time.", actionLabel: "Switch on the terminal" },
      completeOn: { action: "continue" },
    },

    // ---- Act 1: the PC
    { id: "mf_cam_desk", type: "camera", waypointId: "wp_mf_desk", walkSfx: true },
    {
      id: "mf_login",
      type: "device",
      deviceId: "mf_desklogin",
      guide: { title: "Sign in", body: "Click the login screen, fill in your details from the note, then click Sign In.", actionLabel: "Sign In" },
      completeOn: { action: "continue" },
    },
    {
      id: "mf_intranet",
      type: "device",
      deviceId: "mf_pc_intranet",
      guide: { title: "Check the intranet", body: "Click Browser on the desktop." },
      completeOn: { action: "continue" },
    },
    {
      id: "mf_teams",
      type: "device",
      deviceId: "mf_pc_teams",
      guide: { title: "A Teams message", body: "Click the Teams notification." },
      completeOn: { action: "continue" },
    },
    {
      id: "mf_teach_teams_risky",
      type: "teach",
      teach: {
        outcome: "risky",
        body: "The message came from an account marked External. It was not NorthBay's IT team. Replying YES leads to a 'support session': a remote-access tool that gives the attacker control of your PC.",
        spot: ["The External label next to the sender's name", "An unexpected request for a support session", "Time pressure: before you leave, before Monday"],
        protect: ["Check the sender's account and organisation before you reply", "Contact IT through a channel you already know", "Report the message so IT can block the account"],
        remember: "A message inside a trusted app is not proof of who sent it.",
        continueLabel: "Back to work",
        altContinue: { label: "How to investigate the Teams message?", outcomeKey: "details", prominent: true },
      },
    },
    {
      id: "mf_teach_teams_safe",
      type: "teach",
      teach: {
        outcome: "safe",
        body: "The account was marked External: anyone can name a Teams account \"IT Service Desk\". You did not start the support session, and IT now knows about the fake account, so they can block it and warn your colleagues.",
        spot: ["The External label next to the sender's name", "An unexpected request for a support session", "Time pressure: before you leave, before Monday"],
        protect: ["Check the sender's account and organisation", "Contact IT on the number you already know", "Report the message so others are warned"],
        remember: "A message inside a trusted app is not proof of who sent it.",
        continueLabel: "Back to work",
      },
    },
    {
      id: "mf_teach_teams_ignored",
      type: "teach",
      teach: {
        outcome: "safe",
        heading: {
          eyebrow: "SAFE, BUT NOT DONE",
          title: "Ignoring it kept you safe. Reporting protects everyone.",
          spotEyebrow: "WHAT GAVE IT AWAY",
          spotTitle: "The signs in the message",
          protectEyebrow: "NEXT TIME",
          protectTitle: "How to stop it for everyone",
        },
        body: "Ignoring the message kept you safe this time. But the fake account is still active, and the next person might reply. Reporting it lets IT block the account and warn everyone.",
        spot: ["The External label next to the sender's name", "An unexpected request for a support session"],
        protect: ["Report suspicious messages, even when you are busy", "Contact IT through a channel you already know"],
        remember: "Ignoring protects you. Reporting protects everyone.",
        continueLabel: "Back to work",
        altContinue: { label: "How to investigate the Teams message?", outcomeKey: "details", prominent: true },
      },
    },
    {
      id: "mf_teams_review",
      type: "device",
      deviceId: "mf_pc_teams_review",
      guide: { title: "A second look", body: "Click the sender's name in the message to see who really sent it." },
      completeOn: { action: "continue" },
    },
    {
      id: "mf_slack_pc",
      type: "device",
      deviceId: "mf_pc_slack",
      preloadEnvironmentId: "beach",
      guide: { title: "Finish the handover", body: "Click Slack." },
      completeOn: { action: "continue" },
    },

    // ---- Transition + Act 2: the beach bar
    {
      id: "mf_cut_beach",
      type: "camera",
      waypointId: "wp_mf_ocean",
      environmentId: "beach",
      fadeCut: true,
      guide: { title: "Three days later", body: "Alex is on holiday." },
    },
    {
      id: "mf_title_monday",
      type: "title",
      title: "Monday · 14:20",
      autoAdvanceMs: 4200,
      guide: { title: "Three days later", body: "A beach bar, far away from the office." },
    },
    { id: "mf_cam_bar_establish", type: "camera", waypointId: "wp_mf_bar_establish", guide: { title: "The beach bar", body: "Alex has a drink at the bar. The work iPad lies next to it." } },
    { id: "mf_cam_bar_seat", type: "camera", waypointId: "wp_mf_bar_seat", guide: { title: "A notification", body: "The iPad lights up." } },
    {
      id: "mf_bar_ipad",
      type: "device",
      deviceId: "mf_ipad_slack",
      screen: "on",
      lookInPlace: { yawLimitDeg: 80 },
      guide: { title: "A message on your iPad", body: "Drag to look around the bar. When you are ready, tap the Slack notification on the iPad." },
      completeOn: { action: "continue" },
    },
    {
      id: "mf_teach_dm_safe",
      type: "teach",
      teach: {
        outcome: "safe",
        body: "You checked before you opened anything. Nina never sent the message: someone had taken over her Slack account and used what they read in #supplier-renewal, including your holiday.",
        spot: ["An urgent request while you are away", "A file shared through an outside service, not NorthBay's own storage", "A deadline that leaves no time to check"],
        protect: ["Call the colleague on a number you already know", "Report the message to IT Security", "Never approve access to sign off something in a hurry"],
        remember: "Even a real colleague's account can be in the wrong hands.",
        whatIf: "If you had opened the document",
        continueLabel: "See what would have happened",
      },
    },
    {
      id: "mf_consent_replay",
      type: "device",
      deviceId: "mf_ipad_consent_replay",
      forced: true,
      whatIfNote: "If you had opened the document, this is the page and the permission request you would have seen.",
      guide: { title: "The document you would have opened", body: "Click Sign in with Microsoft." },
      completeOn: { action: "continue" },
    },
    {
      id: "mf_consent",
      type: "device",
      deviceId: "mf_ipad_consent",
      guide: { title: "SecureDocs", body: "Click Sign in with Microsoft." },
      completeOn: { action: "continue" },
    },

    // ---- Prevented at the consent screen: the attacker sees "denied"
    { id: "mf_cut_hacker_denied", type: "camera", waypointId: "wp_mf_hacker", environmentId: "hacker", fadeCut: true, guide: { title: "Meanwhile", body: "Somewhere else." } },
    {
      id: "mf_title_attacker_denied",
      type: "title",
      kicker: "The other side",
      title: "Meanwhile, the attacker is waiting",
      autoAdvanceMs: 11000,
      guide: { title: "You are now the attacker", body: "This is a fictional criminal who took over a colleague's Slack account. They want Alex to give their app access to Alex's mailbox and files, to set up a payment fraud. Here is what they could see after your choice." },
    },
    { id: "mf_push_denied", type: "camera", waypointId: "wp_mf_hacker_laptop", guide: { title: "The attacker's laptop", body: "The attack log." } },
    { id: "mf_dash_denied", type: "device", deviceId: "mf_dash_denied", guide: { title: "Authorization denied", body: "Listen, then click Continue." }, completeOn: { action: "continue" } },
    {
      id: "mf_teach_consent_safe",
      type: "teach",
      teach: {
        outcome: "safe",
        body: "You stopped at the permission screen. The Microsoft page was real, but the app was not: a document viewer has no reason to read your mailbox or send email as you.",
        spot: ["An app that asks for far more than the task needs", "Send email on your behalf and lasting access", "An unverified publisher"],
        protect: ["Cancel a permission request you did not expect", "Report it to IT Security", "Open shared work files only in NorthBay's own storage"],
        remember: "A real Microsoft screen does not make an unknown app trustworthy.",
        whatIf: "If Alex had clicked Accept",
        continueLabel: "See what would have happened",
      },
    },

    // ---- Act 3: the attacker's view (real for the successful path, replay otherwise)
    { id: "mf_cut_hacker", type: "camera", waypointId: "wp_mf_hacker", environmentId: "hacker", fadeCut: true, alertSfx: true, whatIfNote: REPLAY_NOTE, guide: { title: "Meanwhile", body: "Somewhere else." } },
    {
      id: "mf_title_attacker",
      type: "title",
      kicker: "The other side",
      title: "While Alex enjoys the holiday",
      autoAdvanceMs: 11000,
      guide: { title: "You are now the attacker", body: "This is a fictional criminal who took over a colleague's Slack account. They want Alex to give their app access to Alex's mailbox and files, to set up a payment fraud. Here is what they could see after your choice." },
    },
    { id: "mf_push", type: "camera", waypointId: "wp_mf_hacker_laptop", whatIfNote: REPLAY_NOTE, guide: { title: "The attacker's laptop", body: "The attack log." } },
    {
      id: "mf_dash_timeline",
      type: "device",
      deviceId: "mf_dash_timeline",
      forced: true,
      whatIfNote: REPLAY_NOTE,
      guide: { title: "How the attack was built", body: "Listen, then click See what the attacker gets." },
      completeOn: { action: "continue" },
    },
    {
      id: "mf_dash_access",
      type: "device",
      deviceId: "mf_dash_access",
      forced: true,
      whatIfNote: REPLAY_NOTE,
      guide: { title: "What the attacker gets", body: "Listen, then click Continue." },
      completeOn: { action: "continue" },
    },

    // ---- Back at the office
    {
      id: "mf_cut_office",
      type: "camera",
      waypointId: "wp_mf_desk_cut",
      environmentId: "office",
      fadeCut: true,
      screen: "off",
      whatIfNote: "If Alex had clicked Accept, this email would be waiting on Wednesday.",
      guide: { title: "Back at the office", body: "Wednesday morning." },
    },
    { id: "mf_title_wednesday", type: "title", title: "Wednesday · 08:40", autoAdvanceMs: 3800, guide: { title: "Back at the office", body: "Alex's first morning after the holiday." } },
    {
      id: "mf_login_back",
      type: "device",
      deviceId: "mf_desklogin",
      forced: true,
      whatIfNote: "If Alex had clicked Accept, this email would be waiting on Wednesday.",
      guide: { title: "Welcome back", body: "Click the login screen, fill in your details, then click Sign In.", actionLabel: "Sign In" },
      completeOn: { action: "continue" },
    },
    {
      id: "mf_desktop_mail",
      type: "device",
      deviceId: "mf_desktop_back",
      forced: true,
      whatIfNote: "If Alex had clicked Accept, this email would be waiting on Wednesday.",
      guide: { title: "A message with your name on it", body: "A new email is waiting. Click Outlook.", actionLabel: "Outlook" },
      completeOn: { action: "open_app", arg: "mail" },
    },
    {
      id: "mf_mail",
      type: "device",
      deviceId: "mf_mail_back",
      forced: true,
      whatIfNote: "If Alex had clicked Accept, this email would be waiting on Wednesday.",
      guide: {
        title: "You never wrote this",
        body: "Open the email from Alex Morgan and read it. Then click Continue.",
        tip: "Look at the Finance reply too.",
      },
      completeOn: { action: "read_email", arg: "fraud" },
    },
    {
      id: "mf_dash_network",
      type: "device",
      deviceId: "mf_dash_network",
      forced: true,
      whatIfNote: REPLAY_NOTE,
      guide: { title: "Who is next", body: "Listen, then click Continue." },
      completeOn: { action: "continue" },
    },
    {
      id: "mf_teach_after_compromised",
      type: "teach",
      teach: {
        outcome: "risky",
        body: "You approved the app on the beach. Your password was never stolen and the iPad was never infected: you gave the attacker's app access to your mailbox and files yourself. What matters now is speed.",
        spot: ["An app asking to read your email and send email as you", "An urgent request sent while you were away", "A file shared through an outside service"],
        protect: [
          "Tell IT straight away, even if you only suspect it",
          "IT revokes the app's permissions and signs you out of every session",
          "IT checks which emails and files the app read, and warns Finance",
        ],
        remember: "Changing your password does not remove an app you have approved. Only IT can revoke it.",
        continueLabel: "Report it to IT",
      },
    },
    {
      id: "mf_teach_after_prevented",
      type: "teach",
      teach: {
        outcome: "recap",
        body: "If it ever happens, and you approve an app by mistake: your password still works, so it can feel like nothing happened. It did. The app keeps its access until IT removes it.",
        spot: ["An app asking to read your email and send email as you", "An urgent request sent while you are away"],
        protect: [
          "Tell IT straight away, even if you only suspect it",
          "IT revokes the app's permissions and signs you out of every session",
          "IT checks which emails and files the app read",
        ],
        remember: "Changing your password does not remove an app you have approved. Only IT can revoke it.",
        continueLabel: "Back to the terminal",
      },
    },

    // ---- Scene 9: the terminal
    { id: "mf_cam_end_compromised", type: "camera", waypointId: "wp_mf_terminal", walkSfx: true, endsWhatIf: true },
    {
      id: "mf_end_compromised",
      type: "consequence",
      consequence: {
        eyebrow: "OUTCOME",
        kicker: "ATTACK SUCCESSFUL · CONTAINED IN TIME",
        title: "Attack successful",
        cctv: false,
        lines: [
          "Alex reports the email to IT at 08:52.",
          "IT finds the SecureDocs consent, revokes the app's permissions, signs Alex out of every session and checks what the app read.",
          "Finance had not paid yet. The supplier confirms its bank details never changed.",
        ],
        continueLabel: "Continue",
      },
    },
    { id: "mf_cam_end_prevented", type: "camera", waypointId: "wp_mf_terminal", walkSfx: true, endsWhatIf: true },
    {
      id: "mf_end_prevented",
      type: "consequence",
      consequence: {
        eyebrow: "OUTCOME",
        kicker: "ATTACK PREVENTED",
        title: "Attack prevented",
        cctv: false,
        lines: [
          "The attacker never got into Alex's mailbox.",
          "IT Security blocked the SecureDocs app for the whole company and secured Nina's Slack account.",
          "One check, made at a beach bar, stopped a payment fraud against Finance.",
        ],
        continueLabel: "Continue",
      },
    },
    {
      id: "mf_debrief_terminal",
      type: "terminal_debrief",
      terminalDebrief: {
        intro: "Here is what happened, and the four things to remember.",
        continueLabel: "Continue to the quiz",
        topics: [
          {
            id: "mf_t_apps",
            dimension: "identity",
            title: "Teams and Slack do not prove who someone is",
            lines: [
              "The 'IT Service Desk' was an external account. Anyone can give a Teams account any name.",
              "Before you reply, check the account and the organisation, and contact IT on a channel you already know.",
            ],
          },
          {
            id: "mf_t_colleague",
            dimension: "identity",
            title: "A real colleague's account can be taken over",
            lines: [
              "Nina's account was real, but the attacker was using it. They read the project chat and learned about your holiday.",
              "An urgent, unexpected request is a reason to call the colleague on a known number.",
            ],
          },
          {
            id: "mf_t_consent",
            dimension: "information",
            title: "A real Microsoft screen, an unknown app",
            lines: [
              "The sign-in page was genuine Microsoft. The app behind it was the attacker's.",
              "A document viewer does not need to read your email, open all your files or send email as you. Cancel and report.",
            ],
          },
          {
            id: "mf_t_report",
            dimension: "operations",
            title: "Verify, report, and act fast",
            lines: [
              "Verify unexpected requests independently, and report anything suspicious straight away.",
              "If you approved an app by mistake, tell IT immediately. IT revokes its permissions, signs out your sessions and checks what was accessed.",
            ],
          },
        ],
      },
    },
    {
      id: "mf_quiz",
      type: "quiz",
      title: "Check what you learned",
      quiz: [
        {
          id: "q_external",
          title: "The Teams message",
          prompt: "A Teams message from \"IT Service Desk\" asks for a quick support session. What tells you it may not be your IT team?",
          options: [
            { id: "a", label: "It is written politely and has no spelling mistakes" },
            { id: "b", label: "The account is marked External", correct: true },
            { id: "c", label: "It arrived in Teams, not by email" },
          ],
          rationale: "Anyone can name an account \"IT Service Desk\". The External label shows it comes from outside the company.",
        },
        {
          id: "q_colleague",
          title: "A colleague's request",
          prompt: "A real colleague sends an urgent request with a document link while you are on holiday. What is the safest first step?",
          options: [
            { id: "a", label: "Call the colleague on a number you already know", correct: true },
            { id: "b", label: "Open the link, because the account is genuine" },
            { id: "c", label: "Reply in the same chat to ask if it is real" },
          ],
          rationale: "If the account is taken over, the attacker also answers in that chat. A known phone number reaches the real person.",
        },
        {
          id: "q_consent",
          title: "The permission screen",
          prompt: "A document viewer asks, on a real Microsoft page, to read your email, read your files and send email on your behalf. What do you do?",
          options: [
            { id: "a", label: "Accept, because the Microsoft page is real" },
            { id: "b", label: "Accept, then change your password afterwards" },
            { id: "c", label: "Cancel and report it to IT Security", correct: true },
          ],
          rationale: "A genuine sign-in page does not make the app trustworthy. Viewing one file never needs your mailbox or send-as rights.",
        },
        {
          id: "q_password",
          title: "After approving an app",
          prompt: "You approved an app by mistake. Why is changing your password not enough?",
          options: [
            { id: "a", label: "The app's access does not depend on your password", correct: true },
            { id: "b", label: "Passwords only change after 24 hours" },
            { id: "c", label: "It is enough; nothing else is needed" },
          ],
          rationale: "An approved app keeps its access until IT revokes the permissions. Tell IT straight away.",
        },
        {
          id: "q_report",
          title: "Reporting",
          prompt: "What does IT do when you report an app you approved by mistake?",
          options: [
            { id: "a", label: "Nothing, unless money has already been lost" },
            { id: "b", label: "Revoke the app's permissions, sign out your sessions and check what was accessed", correct: true },
            { id: "c", label: "Delete your mailbox" },
          ],
          rationale: "Quick reporting lets IT cut the access and warn the people the attacker may target next.",
        },
      ],
    },
    { id: "mf_debrief", type: "debrief", title: "Debrief" },
  ],

  edges: [
    { from: "mf_intro", to: "mf_cam_terminal" },
    { from: "mf_cam_terminal", to: "mf_terminal" },
    { from: "mf_terminal", to: "mf_cam_desk" },
    { from: "mf_cam_desk", to: "mf_login" },
    { from: "mf_login", to: "mf_intranet" },
    { from: "mf_intranet", to: "mf_teams" },
    { from: "mf_teams", to: "mf_teach_teams_risky", on: "replied" },
    { from: "mf_teams", to: "mf_teach_teams_safe", on: "reported" },
    { from: "mf_teams", to: "mf_teach_teams_safe", on: "verified" },
    { from: "mf_teams", to: "mf_teach_teams_ignored", on: "dismissed" },
    { from: "mf_teams", to: "mf_teach_teams_ignored" },
    { from: "mf_teach_teams_risky", to: "mf_teams_review", on: "details" },
    { from: "mf_teach_teams_risky", to: "mf_slack_pc" },
    { from: "mf_teach_teams_safe", to: "mf_slack_pc" },
    { from: "mf_teach_teams_ignored", to: "mf_teams_review", on: "details" },
    { from: "mf_teach_teams_ignored", to: "mf_slack_pc" },
    { from: "mf_teams_review", to: "mf_slack_pc" },
    { from: "mf_slack_pc", to: "mf_cut_beach" },
    { from: "mf_cut_beach", to: "mf_title_monday" },
    { from: "mf_title_monday", to: "mf_cam_bar_establish" },
    { from: "mf_cam_bar_establish", to: "mf_cam_bar_seat" },
    { from: "mf_cam_bar_seat", to: "mf_bar_ipad" },
    { from: "mf_bar_ipad", to: "mf_consent", on: "open" },
    { from: "mf_bar_ipad", to: "mf_teach_dm_safe", on: "verify" },
    { from: "mf_bar_ipad", to: "mf_teach_dm_safe", on: "report" },
    { from: "mf_bar_ipad", to: "mf_consent" },
    { from: "mf_teach_dm_safe", to: "mf_consent_replay" },
    { from: "mf_consent_replay", to: "mf_cut_hacker" },
    { from: "mf_consent", to: "mf_cut_hacker", on: "accept" },
    { from: "mf_consent", to: "mf_cut_hacker_denied", on: "cancel" },
    { from: "mf_consent", to: "mf_cut_hacker" },
    { from: "mf_cut_hacker_denied", to: "mf_title_attacker_denied" },
    { from: "mf_title_attacker_denied", to: "mf_push_denied" },
    { from: "mf_push_denied", to: "mf_dash_denied" },
    { from: "mf_dash_denied", to: "mf_teach_consent_safe" },
    { from: "mf_teach_consent_safe", to: "mf_dash_timeline" },
    { from: "mf_cut_hacker", to: "mf_title_attacker" },
    { from: "mf_title_attacker", to: "mf_push" },
    { from: "mf_push", to: "mf_dash_timeline" },
    { from: "mf_dash_timeline", to: "mf_dash_access" },
    { from: "mf_dash_access", to: "mf_cut_office" },
    { from: "mf_cut_office", to: "mf_title_wednesday" },
    { from: "mf_title_wednesday", to: "mf_login_back" },
    { from: "mf_login_back", to: "mf_desktop_mail" },
    { from: "mf_desktop_mail", to: "mf_mail" },
    { from: "mf_mail", to: "mf_dash_network" },
    { from: "mf_dash_network", to: "mf_teach_after_prevented", on: "replay" },
    { from: "mf_dash_network", to: "mf_teach_after_compromised" },
    { from: "mf_teach_after_compromised", to: "mf_cam_end_compromised" },
    { from: "mf_cam_end_compromised", to: "mf_end_compromised" },
    { from: "mf_teach_after_prevented", to: "mf_cam_end_prevented" },
    { from: "mf_cam_end_prevented", to: "mf_end_prevented" },
    { from: "mf_end_compromised", to: "mf_debrief_terminal" },
    { from: "mf_end_prevented", to: "mf_debrief_terminal" },
    { from: "mf_debrief_terminal", to: "mf_quiz" },
    { from: "mf_quiz", to: "mf_debrief" },
  ],
};
